/**
 * clinicTimezone.ts — single source of truth for the active clinic IANA
 * timezone, plus helpers that interpret/format timestamps in it.
 *
 * WHY a module singleton (not a hook): timezone interpretation is needed in
 * pure utils, firebase cache code, and driven adapters that have no React
 * context. Threading `clinicTimezone` through every signature would touch
 * hundreds of call sites. Instead the clinic TZ is mirrored here from redux
 * (see the store subscription in `src/redux/index.ts`) and read synchronously.
 *
 * IMPORTANT — no AppController import. This is a leaf module so that low-level
 * adapters (e.g. cdlRepository, which AppController itself constructs) can
 * import it without creating a circular dependency. We extend dayjs's utc +
 * timezone plugins here directly; `dayjs.extend` is idempotent and operates on
 * the shared dayjs singleton, so this is safe even though AppController also
 * extends them.
 *
 * FALLBACK POLICY (product decision): when the clinic TZ is unknown/not yet
 * loaded, every helper falls back to the BROWSER timezone — i.e. the same
 * behavior the app had before this module existed. Pass nothing special to get
 * that; it happens automatically when `_clinicTimezone` is undefined.
 */
import dayjs, { Dayjs, ConfigType } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
// Required for `dayjs(value, formatString)` parsing (e.g. clinicWallClock with a
// custom format, or `dayjs(date, "YYYY-MM-DD")`); without it the format arg is
// ignored and non-ISO wall-clock strings can parse incorrectly.
dayjs.extend(customParseFormat);

let _clinicTimezone: string | undefined;

/**
 * Set the active clinic IANA timezone (e.g. "America/New_York"). Called from
 * the redux store subscription whenever `clinic.userClinic.timezone` changes.
 * Pass `undefined`/empty to clear (helpers then fall back to browser TZ).
 */
export function setActiveClinicTimezone(tz?: string | null): void {
  _clinicTimezone = tz || undefined;
}

/** The active clinic IANA timezone, or `undefined` if unknown (→ browser TZ). */
export function getClinicTimezone(): string | undefined {
  return _clinicTimezone;
}

/**
 * The timezone helpers should resolve against: clinic TZ when known, otherwise
 * the browser's resolved IANA zone, otherwise UTC. Returning a concrete zone
 * (rather than `undefined`) keeps formatting deterministic for callers that
 * persist the value (e.g. cache `timezone` fields).
 */
export function getEffectiveTimezone(): string {
  if (_clinicTimezone) return _clinicTimezone;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Interpret an absolute instant (ms epoch, Date, ISO string, or Dayjs) in the
 * clinic timezone. Falls back to browser-local when clinic TZ is unknown.
 *
 * Use this for the common "I have a real moment, what is its clinic wall-clock
 * hour/day" question — the replacement for `new Date(x).getHours()` etc.
 */
export function clinicDayjs(input?: ConfigType): Dayjs {
  const base = input === undefined ? dayjs() : dayjs(input);
  return _clinicTimezone ? base.tz(_clinicTimezone) : base;
}

/** "Now" as a Dayjs in the clinic timezone (browser-local fallback). */
export function clinicNow(): Dayjs {
  return clinicDayjs();
}

/**
 * Parse a wall-clock datetime string (no TZ suffix, e.g. "2026-06-01 22:00:00"
 * or with a custom `format`) AS clinic-local time, i.e. keepLocalTime. Use when
 * the input is a wall-clock the clinic observed, not an absolute UTC instant.
 */
export function clinicWallClock(input: string, format?: string): Dayjs {
  const base = format ? dayjs(input, format) : dayjs(input);
  // @ts-ignore — dayjs tz plugin accepts keepLocalTime as the 2nd arg
  return _clinicTimezone ? base.tz(_clinicTimezone, true) : base;
}

/** Clinic-TZ hour-of-day (0–23) for an absolute instant. */
export function clinicHourOf(input: ConfigType): number {
  const d = clinicDayjs(input);
  return d.isValid() ? d.hour() : NaN;
}

/** Clinic-TZ calendar-day key ("YYYY-MM-DD") for an absolute instant. */
export function clinicDateKey(input?: ConfigType): string {
  return clinicDayjs(input).format("YYYY-MM-DD");
}

/** Convenience: clinic-TZ formatted string for an absolute instant. */
export function clinicFormat(input: ConfigType, template: string): string {
  return clinicDayjs(input).format(template);
}
